import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, CalendarDays, Ticket, Clock, Mail, LogOut, 
  Shield, ChevronRight, ChevronDown, UserPlus, UserMinus, Trash2, Send,
  TrendingUp, Menu, X, Search, Copy, Check, Activity, PhoneCall, Building2, Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import AdminVoiceAgents from "@/pages/AdminVoiceAgents";
import AdminClients from "@/pages/AdminClients";
import AdminPackages from "@/pages/AdminPackages";
import { auth } from "@/lib/firebase";
import { signOut } from "firebase/auth";

const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

// Sidebar menu items
const menuItems = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "users", label: "Users", icon: Users },
  { id: "bookings", label: "Bookings", icon: Ticket },
  { id: "waitlist", label: "Waitlist", icon: Clock },
  { id: "analytics", label: "Visits", icon: Activity },
  { id: "voice", label: "Voice Agents", icon: PhoneCall },
  { id: "clients", label: "B2B Clients", icon: Building2 },
  { id: "packages", label: "Packages", icon: Package },
  { id: "email", label: "Email", icon: Mail },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [fakeLoading, setFakeLoading] = useState(null);
  const [fakeInputs, setFakeInputs] = useState({});
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Manual email state
  const [manualEmails, setManualEmails] = useState("");
  const [manualSubject, setManualSubject] = useState("");
  const [manualMessage, setManualMessage] = useState("");
  const [sendingManualEmail, setSendingManualEmail] = useState(false);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const headers = await authHeaders();
        
        const dashResp = await fetch(`${API}/admin/dashboard`, { headers });
        if (dashResp.status === 403) {
          setAccessDenied(true);
          return;
        }
        if (dashResp.ok) {
          setStats(await dashResp.json());
          setLoading(false);
        }
        
        const [usersResp, eventsResp, bookingsResp, waitlistResp] = await Promise.all([
          fetch(`${API}/admin/users`, { headers }),
          fetch(`${API}/admin/events`, { headers }),
          fetch(`${API}/admin/bookings`, { headers }),
          fetch(`${API}/admin/waitlist`, { headers }),
        ]);

        if (usersResp.ok) setUsers(await usersResp.json());
        if (eventsResp.ok) setEvents(await eventsResp.json());
        if (bookingsResp.ok) setBookings(await bookingsResp.json());
        if (waitlistResp.ok) setWaitlist(await waitlistResp.json());
      } catch (error) {
        console.error("Admin load failed:", error);
        toast.error("Failed to load admin data");
        setLoading(false);
      }
    };
    if (user) loadAll();
  }, [user]);

  // ----- Helpers that work for games, series, and trips -----
  const _endpointFor = (event) => {
    const t = event.type || "game";
    const id = event.id || event.game_id || event.series_id || event.trip_id;
    // "series" is its own plural — don't append an 's' (that would be "seriess").
    const path = t === "series" ? "series" : `${t}s`;
    return `${API}/${path}/${id}/fake-attendees`;
  };
  const _rowMatches = (e, event) => (e.id || e.game_id) === (event.id || event.game_id);

  const handleAddFake = async (event, count) => {
    if (!count || count < 1) {
      toast.error("Enter a valid number");
      return;
    }
    const rowKey = event.id || event.game_id;
    setFakeLoading(rowKey);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const resp = await fetch(_endpointFor(event), {
        method: "POST",
        headers,
        body: JSON.stringify({ count: parseInt(count, 10) }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const addedCount = data.added || 0;
        toast.success(`Added ${addedCount} fake ${event.type === "trip" ? "guests" : event.type === "series" ? "pass holders" : "attendees"}`);
        setEvents(events.map(e => _rowMatches(e, event) ? {
          ...e,
          current_players: (e.current_players || 0) + addedCount,
          fake_bookings: (e.fake_bookings || 0) + addedCount,
        } : e));
        setFakeInputs({ ...fakeInputs, [rowKey]: "" });
      } else {
        const error = await resp.json().catch(() => ({}));
        toast.error(error.detail || "Failed to add fake attendees");
      }
    } catch (error) {
      toast.error("Failed to add fake attendees");
    } finally {
      setFakeLoading(null);
    }
  };

  const handleRemoveFake = async (event, count) => {
    if (!count || count < 1) {
      toast.error("Enter a valid number");
      return;
    }
    const rowKey = event.id || event.game_id;
    setFakeLoading(rowKey);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const resp = await fetch(`${_endpointFor(event)}/remove`, {
        method: "POST",
        headers,
        body: JSON.stringify({ count: parseInt(count, 10) }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const removedCount = data.removed || 0;
        toast.success(`Removed ${removedCount} fake attendees`);
        setEvents(events.map(e => _rowMatches(e, event) ? {
          ...e,
          current_players: Math.max(0, (e.current_players || 0) - removedCount),
          fake_bookings: Math.max(0, (e.fake_bookings || 0) - removedCount),
        } : e));
        setFakeInputs({ ...fakeInputs, [rowKey]: "" });
      } else {
        const error = await resp.json().catch(() => ({}));
        toast.error(error.detail || "Failed to remove fake attendees");
      }
    } catch (error) {
      toast.error("Failed to remove fake attendees");
    } finally {
      setFakeLoading(null);
    }
  };

  const handleDeleteGame = async (event) => {
    if (!confirm("Are you sure you want to delete this event? This cannot be undone.")) return;
    const t = event.type || "game";
    const id = event.id || event.game_id;
    if (t !== "game") {
      toast.error("Use the host dashboard to cancel series/trips for now");
      return;
    }
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${API}/games/${id}`, { method: "DELETE", headers });
      if (resp.ok) {
        toast.success("Event deleted");
        setEvents(events.filter(e => !_rowMatches(e, event)));
      } else {
        toast.error("Failed to delete event");
      }
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleSendEventEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    setSendingEmail(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const resp = await fetch(`${API}/admin/send-event-email`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          game_id: emailTarget.game_id,
          subject: emailSubject,
          message: emailMessage
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.sent_count > 0) {
          toast.success(`Email sent to ${data.sent_count} attendee${data.sent_count !== 1 ? 's' : ''}!`);
        } else {
          toast.error(`Found ${data.reservations_found || 0} reservations but couldn't match them to user accounts`);
        }
        setShowEmailModal(false);
      } else {
        const error = await resp.json().catch(() => ({}));
        toast.error(error.detail || "Failed to send emails");
      }
    } catch (error) {
      toast.error("Failed to send emails");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendManualEmail = async () => {
    if (!manualEmails.trim() || !manualSubject.trim() || !manualMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    const emailList = manualEmails
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes("@"));
    
    if (emailList.length === 0) {
      toast.error("Please enter at least one valid email address");
      return;
    }
    
    setSendingManualEmail(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const resp = await fetch(`${API}/admin/send-manual-email`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          emails: emailList,
          subject: manualSubject,
          message: manualMessage
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        toast.success(`Email sent to ${data.sent_count} recipient${data.sent_count !== 1 ? 's' : ''}!`);
        setManualEmails("");
        setManualSubject("");
        setManualMessage("");
      } else {
        const error = await resp.json().catch(() => ({}));
        toast.error(error.detail || "Failed to send emails");
      }
    } catch (error) {
      toast.error("Failed to send emails");
    } finally {
      setSendingManualEmail(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="admin-denied">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <Shield className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin privileges.</p>
          <Button onClick={() => navigate("/")} variant="outline">Go Home</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewSection stats={stats} users={users} events={events} bookings={bookings} />;
      case "events":
        return (
          <EventsSection 
            events={events} 
            fakeInputs={fakeInputs} 
            setFakeInputs={setFakeInputs}
            fakeLoading={fakeLoading}
            expandedEvent={expandedEvent}
            setExpandedEvent={setExpandedEvent}
            handleAddFake={handleAddFake}
            handleRemoveFake={handleRemoveFake}
            handleDeleteGame={handleDeleteGame}
            setEmailTarget={setEmailTarget}
            setEmailSubject={setEmailSubject}
            setEmailMessage={setEmailMessage}
            setShowEmailModal={setShowEmailModal}
            navigate={navigate}
          />
        );
      case "users":
        return <UsersSection users={users} />;
      case "bookings":
        return <BookingsSection bookings={bookings} navigate={navigate} />;
      case "waitlist":
        return <WaitlistSection waitlist={waitlist} navigate={navigate} />;
      case "analytics":
        return <AnalyticsSection />;
      case "voice":
        return <AdminVoiceAgents />;
      case "clients":
        return <AdminClients />;
      case "packages":
        return <AdminPackages />;
      case "email":
        return (
          <EmailSection 
            manualEmails={manualEmails}
            setManualEmails={setManualEmails}
            manualSubject={manualSubject}
            setManualSubject={setManualSubject}
            manualMessage={manualMessage}
            setManualMessage={setManualMessage}
            sendingManualEmail={sendingManualEmail}
            handleSendManualEmail={handleSendManualEmail}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Mobile menu button */}
      <button 
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card border border-border rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-card border-r border-border
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Management</p>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors mb-1
                  ${isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border space-y-2">
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Header */}
        <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="lg:ml-0 ml-10">
              <h1 className="text-xl font-bold">
                {menuItems.find(m => m.id === activeSection)?.label || 'Dashboard'}
              </h1>
              <p className="text-sm text-muted-foreground">Welcome back. Here's what's happening with Pouls.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              View Site
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {renderContent()}
        </div>
      </main>

      {/* Event Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email to Attendees</DialogTitle>
            <DialogDescription>
              Send an email to all {emailTarget?.real_bookings || 0} attendees of "{emailTarget?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Important update about your game..."
              />
            </div>
            <div>
              <Label htmlFor="email-message">Message</Label>
              <Textarea
                id="email-message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Write your message here..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button onClick={handleSendEventEmail} disabled={sendingEmail}>
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ SECTION COMPONENTS ============

const OverviewSection = ({ stats, users, events, bookings }) => {
  const recentUsers = users.slice(0, 5);
  const recentBookings = bookings.slice(0, 5);
  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">+{stats.recent_signups_30d} last 30 days</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold">{stats.total_games}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Real Bookings</p>
                  <p className="text-2xl font-bold">{stats.total_bookings}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">On Waitlist</p>
                  <p className="text-2xl font-bold">{stats.total_waitlist || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Recent Signups</h3>
            <div className="space-y-3">
              {recentUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users yet</p>
              ) : (
                recentUsers.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      {u.picture && <AvatarImage src={u.picture} />}
                      <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{timeAgo(u.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Bookings */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Recent Bookings</h3>
            <div className="space-y-3">
              {recentBookings.length === 0 ? (
                <p className="text-sm text-muted-foreground">No bookings yet</p>
              ) : (
                recentBookings.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      {b.user_picture && <AvatarImage src={b.user_picture} />}
                      <AvatarFallback className="text-xs">{getInitials(b.user_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.user_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.game_title}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{timeAgo(b.booked_at)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-4">Upcoming Events</h3>
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming events</p>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map((e) => (
                <div key={e.game_id} className="flex items-center gap-4 p-2 rounded-lg hover:bg-muted">
                  <div className="flex-1">
                    <p className="font-medium">{e.title}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(e.date)} · {e.city}</p>
                  </div>
                  <Badge variant="outline">{e.current_players}/{e.max_players}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const EventsSection = ({ 
  events, fakeInputs, setFakeInputs, fakeLoading, expandedEvent, setExpandedEvent,
  handleAddFake, handleRemoveFake, handleDeleteGame, 
  setEmailTarget, setEmailSubject, setEmailMessage, setShowEmailModal, navigate 
}) => {
  const [copyingId, setCopyingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyEmails = async (event) => {
    const type = event.type || "game";
    const id = event.id || event.game_id || event.series_id || event.trip_id;
    const rowKey = event.id || event.game_id;
    setCopyingId(rowKey);
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${API}/admin/events/${type}/${id}/emails`, { headers });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to fetch emails");
        return;
      }
      const data = await resp.json();
      if (!data.count) {
        toast.info("No real attendees to copy yet");
        return;
      }
      const text = data.emails.join(", ");
      // Prefer the Clipboard API, fall back to a hidden textarea for older browsers.
      let ok = false;
      try {
        await navigator.clipboard.writeText(text);
        ok = true;
      } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try { ok = document.execCommand("copy"); } catch { ok = false; }
        document.body.removeChild(ta);
      }
      if (ok) {
        toast.success(`Copied ${data.count} email${data.count !== 1 ? "s" : ""} to clipboard`);
        setCopiedId(rowKey);
        setTimeout(() => setCopiedId((v) => (v === rowKey ? null : v)), 2000);
      } else {
        toast.error("Copy blocked by browser — emails logged in console");
        console.log("Attendee emails:", text);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to copy emails");
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">All Events ({events.length})</h2>
      </div>
      
      {events.length === 0 ? (
        <p className="text-muted-foreground">No events yet.</p>
      ) : (
        <div className="space-y-3">
          {events.map((e) => {
            const rowKey = e.id || e.game_id;
            const type = e.type || "game";
            const viewUrl = type === "trip" ? `/trips/${rowKey}` : type === "series" ? `/series/${rowKey}` : `/game/${rowKey}`;
            const typeBadge = type === "trip"
              ? { label: "Trip", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" }
              : type === "series"
                ? { label: "Series", cls: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" }
                : { label: "Game", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300" };
            const expanded = expandedEvent === rowKey;
            return (
            <Card key={rowKey}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {e.image_url && (
                    <img src={e.image_url} alt="" className="w-20 h-14 object-cover rounded-lg shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium ${typeBadge.cls}`}>
                            {typeBadge.label}
                          </span>
                          <h3 className="font-semibold truncate">{e.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(e.date)}{e.time_start ? ` · ${e.time_start}` : ""}{e.city ? ` · ${e.city}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline">{e.current_players}/{e.max_players}</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedEvent(expanded ? null : rowKey)}
                        >
                          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Real: {e.real_bookings || 0}</span>
                      <span>Fake: {e.fake_bookings || 0}</span>
                      {e.price > 0 && <span>{({EUR:"€",GBP:"£",USD:"$"}[e.currency]) || "$"}{e.price}</span>}
                    </div>
                  </div>
                </div>

                {/* Expanded Section */}
                {expanded && (
                  <div className="mt-4 pt-4 border-t border-border space-y-4">
                    {/* Fake Attendees Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        placeholder="Count"
                        className="w-20 h-8"
                        value={fakeInputs[rowKey] || ""}
                        onChange={(ev) => setFakeInputs({ ...fakeInputs, [rowKey]: ev.target.value })}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddFake(e, fakeInputs[rowKey])}
                        disabled={fakeLoading === rowKey}
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" />
                        Add Fake
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveFake(e, fakeInputs[rowKey])}
                        disabled={fakeLoading === rowKey}
                      >
                        <UserMinus className="w-3.5 h-3.5 mr-1" />
                        Remove Fake
                      </Button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => navigate(viewUrl)}>
                        View Event
                      </Button>
                      {(e.real_bookings || 0) > 0 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopyEmails(e)}
                          disabled={copyingId === rowKey}
                          data-testid={`copy-emails-${rowKey}`}
                        >
                          {copiedId === rowKey ? (
                            <><Check className="w-3.5 h-3.5 mr-1 text-emerald-500" />Copied</>
                          ) : (
                            <><Copy className="w-3.5 h-3.5 mr-1" />{copyingId === rowKey ? "Copying…" : `Copy ${e.real_bookings} Email${e.real_bookings !== 1 ? "s" : ""}`}</>
                          )}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" disabled title="No real attendees yet">
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          Copy Emails
                        </Button>
                      )}
                      {type === "game" && (e.real_bookings || 0) > 0 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEmailTarget({ game_id: rowKey, title: e.title, real_bookings: e.real_bookings });
                            setEmailSubject("");
                            setEmailMessage("");
                            setShowEmailModal(true);
                          }}
                          data-testid={`email-attendees-${rowKey}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" />
                          Email {e.real_bookings} Attendee{e.real_bookings !== 1 ? "s" : ""}
                        </Button>
                      ) : type === "game" ? (
                        <Button size="sm" variant="outline" disabled title="No paid attendees yet">
                          <Mail className="w-3.5 h-3.5 mr-1" />
                          Email Attendees
                        </Button>
                      ) : null}
                      {type === "game" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteGame(e)}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )})}
        </div>
      )}
    </div>
  );
};

const UsersSection = ({ users }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedUser, setSelectedUser] = useState(null);
  const [walletAmount, setWalletAmount] = useState("");
  const [walletCurrency, setWalletCurrency] = useState("EUR");
  const [walletReason, setWalletReason] = useState("");
  const [walletAdjusting, setWalletAdjusting] = useState(false);

  const handleWalletAdjust = async () => {
    const amt = parseFloat(walletAmount);
    if (!selectedUser || isNaN(amt) || amt === 0) {
      toast.error("Enter a non-zero amount");
      return;
    }
    if (!walletReason.trim()) {
      toast.error("Please add a reason");
      return;
    }
    setWalletAdjusting(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const r = await fetch(`${API}/admin/wallet/adjust`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          amount: amt,
          currency: walletCurrency,
          reason: walletReason.trim(),
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const sym = walletCurrency === "GBP" ? "£" : walletCurrency === "EUR" ? "€" : "$";
        toast.success(`Wallet ${amt > 0 ? "credited" : "debited"} — new balance: ${sym}${data.new_balance.toFixed(2)}`);
        setWalletAmount("");
        setWalletReason("");
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Adjustment failed");
      }
    } catch (e) {
      toast.error("Adjustment failed");
    } finally {
      setWalletAdjusting(false);
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(u => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "oldest":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "most_bookings":
          return (b.booking_count || 0) - (a.booking_count || 0);
        case "name_az":
          return (a.name || "").localeCompare(b.name || "");
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-lg font-semibold">All Users ({filteredUsers.length})</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64"
          />
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-background"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="most_bookings">Most bookings</option>
            <option value="name_az">Name A-Z</option>
          </select>
        </div>
      </div>
      
      {filteredUsers.length === 0 ? (
        <p className="text-muted-foreground">{search ? "No users match your search." : "No users yet."}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredUsers.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => setSelectedUser(u)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-muted transition-colors text-left"
                >
                  <Avatar className="h-10 w-10">
                    {u.picture && <AvatarImage src={u.picture} />}
                    <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{u.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm">{u.booking_count || 0} bookings</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(u.created_at)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Profile Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {selectedUser.picture && <AvatarImage src={selectedUser.picture} />}
                  <AvatarFallback className="text-lg">{getInitials(selectedUser.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Bookings</p>
                  <p className="text-xl font-bold">{selectedUser.booking_count || 0}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Joined</p>
                  <p className="text-sm font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">Details</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">User ID</span>
                    <span className="font-mono text-xs">{selectedUser.user_id?.slice(0, 12)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span>{selectedUser.email}</span>
                  </div>
                  {selectedUser.is_admin && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role</span>
                      <Badge variant="secondary">Admin</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Wallet adjustment */}
              <div className="space-y-3 pt-4 border-t">
                <p className="text-sm font-medium">Adjust Wallet</p>
                <p className="text-xs text-muted-foreground">Use a positive amount to credit, negative to debit.</p>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    className="col-span-2"
                    data-testid="wallet-amount"
                  />
                  <select
                    value={walletCurrency}
                    onChange={(e) => setWalletCurrency(e.target.value)}
                    className="rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="wallet-currency"
                  >
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <Input
                  placeholder="Reason (e.g. Apology credit, manual refund)"
                  value={walletReason}
                  onChange={(e) => setWalletReason(e.target.value)}
                  data-testid="wallet-reason"
                />
                <Button
                  onClick={handleWalletAdjust}
                  disabled={walletAdjusting || !walletAmount || !walletReason}
                  className="w-full"
                  data-testid="wallet-adjust-btn"
                >
                  {walletAdjusting ? "Adjusting..." : (parseFloat(walletAmount || 0) >= 0 ? "Credit Wallet" : "Debit Wallet")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BookingsSection = ({ bookings, navigate }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Filter and sort bookings
  const filteredBookings = bookings
    .filter(b => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        b.user_name?.toLowerCase().includes(q) ||
        b.user_email?.toLowerCase().includes(q) ||
        b.game_title?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.booked_at || 0) - new Date(a.booked_at || 0);
        case "oldest":
          return new Date(a.booked_at || 0) - new Date(b.booked_at || 0);
        case "event_date":
          return new Date(b.game_date || 0) - new Date(a.game_date || 0);
        case "user_az":
          return (a.user_name || "").localeCompare(b.user_name || "");
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-lg font-semibold">All Bookings ({filteredBookings.length})</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <Input
            placeholder="Search user or event..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64"
          />
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-background"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="event_date">By event date</option>
            <option value="user_az">User A-Z</option>
          </select>
        </div>
      </div>
      
      {filteredBookings.length === 0 ? (
        <p className="text-muted-foreground">{search ? "No bookings match your search." : "No bookings yet."}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredBookings.map((b, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedBooking(b)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-muted transition-colors text-left"
                >
                  <Avatar className="h-10 w-10">
                    {b.user_picture && <AvatarImage src={b.user_picture} />}
                    <AvatarFallback>{getInitials(b.user_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{b.user_name}</p>
                    <p className="text-sm text-muted-foreground truncate">{b.user_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium truncate max-w-[150px]">{b.game_title}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(b.game_date)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                <Avatar className="h-12 w-12">
                  {selectedBooking.user_picture && <AvatarImage src={selectedBooking.user_picture} />}
                  <AvatarFallback>{getInitials(selectedBooking.user_name)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold">{selectedBooking.user_name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedBooking.user_email}</p>
                </div>
              </div>
              
              {/* Event Info */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Event</p>
                <div className="p-3 border border-border rounded-lg">
                  <p className="font-medium">{selectedBooking.game_title}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedBooking.game_date)} · {selectedBooking.game_city}
                  </p>
                </div>
              </div>
              
              {/* Booking Info */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Booking Info</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Booked on</span>
                    <span>{formatDate(selectedBooking.booked_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant="secondary">{selectedBooking.status || "confirmed"}</Badge>
                  </div>
                </div>
              </div>
              
              <Button 
                className="w-full"
                onClick={() => {
                  navigate(`/game/${selectedBooking.game_id}`);
                  setSelectedBooking(null);
                }}
              >
                View Event
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const WaitlistSection = ({ waitlist, navigate }) => {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Waitlist ({waitlist.length})</h2>
      
      {waitlist.length === 0 ? (
        <p className="text-muted-foreground">No one on waitlist.</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {waitlist.map((w, i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                  <Avatar className="h-10 w-10">
                    {w.user_picture && <AvatarImage src={w.user_picture} />}
                    <AvatarFallback>{getInitials(w.user_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{w.user_name}</p>
                    <p className="text-sm text-muted-foreground truncate">{w.user_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <button 
                      onClick={() => navigate(`/game/${w.game_id}`)}
                      className="text-sm font-medium hover:text-primary truncate max-w-[150px] block"
                    >
                      {w.game_title}
                    </button>
                    <p className="text-xs text-muted-foreground">Position #{w.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AnalyticsSection = () => {
  const [range, setRange] = useState("week");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const useCustomRange = Boolean(fromDate || toDate);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const headers = await authHeaders();
        const params = new URLSearchParams();
        if (useCustomRange) {
          if (fromDate) params.set("from_date", fromDate);
          if (toDate) params.set("to_date", toDate);
        } else {
          params.set("range", range);
        }
        const resp = await fetch(`${API}/admin/analytics/visits?${params}`, { headers });
        if (resp.ok) {
          const d = await resp.json();
          if (!cancelled) setData(d);
        } else if (!cancelled) {
          const err = await resp.json().catch(() => ({}));
          toast.error(err.detail || "Failed to load analytics");
        }
      } catch {
        if (!cancelled) toast.error("Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range, fromDate, toDate, useCustomRange]);

  const ranges = [
    { id: "day", label: "24h" },
    { id: "week", label: "7d" },
    { id: "month", label: "30d" },
    { id: "quarter", label: "90d" },
    { id: "year", label: "1y" },
    { id: "all", label: "All" },
  ];

  const todayStr = new Date().toISOString().slice(0, 10);
  const clearCustom = () => {
    setFromDate("");
    setToDate("");
  };
  const pickToday = () => {
    setFromDate(todayStr);
    setToDate(todayStr);
  };
  const pickYesterday = () => {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const s = y.toISOString().slice(0, 10);
    setFromDate(s);
    setToDate(s);
  };

  const daily = data?.daily || [];
  const maxVisits = Math.max(1, ...daily.map((d) => d.visits || 0));

  const fmtDay = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-");
      return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch {
      return iso;
    }
  };

  const rangeLabelMap = {
    day: "the last 24 hours",
    week: "the last 7 days",
    month: "the last 30 days",
    quarter: "the last 90 days",
    year: "the last 12 months",
    all: "all time",
  };
  const sinceLabel = data?.since
    ? new Date(data.since).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;
  const earliestLabel = data?.earliest_visit
    ? new Date(data.earliest_visit).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;
  // "Ineffective filter" = user picked a range that reaches further back than
  // the earliest recorded visit, so day/week/month/all all look identical.
  const filterIsIneffective = !!(
    data?.earliest_visit &&
    data?.total_pageviews === data?.total_all_time &&
    range !== "day"
  );
  const historyMs = data?.earliest_visit ? (Date.now() - new Date(data.earliest_visit).getTime()) : 0;
  const historyDays = Math.max(0, Math.floor(historyMs / (1000 * 60 * 60 * 24)));
  const historyHours = Math.max(0, Math.floor(historyMs / (1000 * 60 * 60)));

  return (
    <div className="space-y-4" data-testid="analytics-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Website visits</h2>
          <p className="text-xs text-muted-foreground">
            {loading ? (
              "Loading…"
            ) : useCustomRange ? (
              <>
                Showing custom range
                {fromDate && <> from <span className="text-foreground font-medium">{new Date(fromDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span></>}
                {toDate && <> to <span className="text-foreground font-medium">{new Date(toDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span></>}
              </>
            ) : (
              <>
                Showing <span className="text-foreground font-medium">{rangeLabelMap[range] || range}</span>
                {sinceLabel && <> · since <span className="text-foreground font-medium">{sinceLabel}</span></>}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 p-1 rounded-full border border-border bg-muted/40" data-testid="analytics-range-tabs">
          {ranges.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { clearCustom(); setRange(r.id); }}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${!useCustomRange && range === r.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              data-testid={`analytics-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date-range picker */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="analytics-from" className="text-xs text-muted-foreground">From</Label>
              <Input
                id="analytics-from"
                type="date"
                value={fromDate}
                max={todayStr}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[160px] h-9"
                data-testid="analytics-from-date"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="analytics-to" className="text-xs text-muted-foreground">To</Label>
              <Input
                id="analytics-to"
                type="date"
                value={toDate}
                min={fromDate || undefined}
                max={todayStr}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[160px] h-9"
                data-testid="analytics-to-date"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={pickToday} data-testid="analytics-pick-today">Today</Button>
              <Button size="sm" variant="outline" onClick={pickYesterday} data-testid="analytics-pick-yesterday">Yesterday</Button>
              {useCustomRange && (
                <Button size="sm" variant="ghost" onClick={clearCustom} data-testid="analytics-clear-custom">
                  <X className="w-3.5 h-3.5 mr-1" />Clear
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground ml-auto">Leave both blank to use the shortcut pills.</p>
          </div>
        </CardContent>
      </Card>

      {/* Explanation banner — appears when the user picked a range wider than the actual history. */}
      {filterIsIneffective && !loading && (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-testid="analytics-history-hint"
        >
          <div className="text-lg leading-none mt-0.5">💡</div>
          <div className="text-sm text-foreground">
            <div className="font-semibold">Filters look identical? That&apos;s expected right now.</div>
            <div className="text-muted-foreground mt-0.5">
              Your first recorded visit was <span className="text-foreground font-medium">{earliestLabel}</span>
              {" "}— only {historyDays > 0 ? `${historyDays} day${historyDays !== 1 ? "s" : ""}` : `${historyHours} hour${historyHours !== 1 ? "s" : ""}`} of history so far.
              Every filter wider than that returns the same numbers because there simply isn&apos;t older data yet.
              Come back in a few days and the ranges will diverge.
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Pageviews</p>
          <p className="text-3xl font-bold mt-1" data-testid="analytics-total-pageviews">{loading ? "…" : (data?.total_pageviews ?? 0).toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Unique visitors</p>
          <p className="text-3xl font-bold mt-1" data-testid="analytics-unique-sessions">{loading ? "…" : (data?.unique_sessions ?? 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">Deduped by session (30-min window)</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Logged-in users</p>
          <p className="text-3xl font-bold mt-1" data-testid="analytics-unique-users">{loading ? "…" : (data?.unique_users ?? 0).toLocaleString()}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Visits per day</h3>
            <span className="text-xs text-muted-foreground">{daily.length} day{daily.length !== 1 ? "s" : ""}</span>
          </div>
          {loading ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : daily.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">No visits recorded in this range yet.</div>
          ) : (
            <div className="flex items-end gap-1 h-40 overflow-x-auto pb-1" data-testid="analytics-daily-chart">
              {daily.map((d) => {
                const pct = Math.max(3, Math.round(((d.visits || 0) / maxVisits) * 100));
                return (
                  <div key={d.day} className="flex flex-col items-center gap-1 min-w-[36px]" title={`${fmtDay(d.day)}: ${d.visits} visit${d.visits !== 1 ? "s" : ""} · ${d.unique_sessions} unique`}>
                    <div className="w-full flex-1 flex items-end">
                      <div
                        className="w-full rounded-t-md bg-primary/80 hover:bg-primary transition-colors"
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtDay(d.day)}</span>
                    <span className="text-[10px] text-foreground font-semibold">{d.visits}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">Top pages</h3>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (data?.top_paths || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No data yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="analytics-top-paths">
              {(data.top_paths || []).map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm gap-2">
                  <span className="truncate text-foreground" title={p.path}>{p.path}</span>
                  <Badge variant="outline" className="shrink-0">{p.count}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">Top referrers</h3>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (data?.top_referrers || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No external referrers recorded yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="analytics-top-referrers">
              {(data.top_referrers || []).map((r) => (
                <li key={r.referrer} className="flex items-center justify-between text-sm gap-2">
                  <span className="truncate text-foreground" title={r.referrer}>{r.referrer}</span>
                  <Badge variant="outline" className="shrink-0">{r.count}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
};


const EmailSection = ({ 
  manualEmails, setManualEmails, 
  manualSubject, setManualSubject, 
  manualMessage, setManualMessage,
  sendingManualEmail, handleSendManualEmail 
}) => {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Send Email</h2>
      
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label htmlFor="manual-emails">Recipients (one email per line or comma-separated)</Label>
            <Textarea
              id="manual-emails"
              value={manualEmails}
              onChange={(e) => setManualEmails(e.target.value)}
              placeholder="email1@example.com&#10;email2@example.com"
              rows={4}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="manual-subject">Subject</Label>
            <Input
              id="manual-subject"
              value={manualSubject}
              onChange={(e) => setManualSubject(e.target.value)}
              placeholder="Email subject..."
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="manual-message">Message</Label>
            <Textarea
              id="manual-message"
              value={manualMessage}
              onChange={(e) => setManualMessage(e.target.value)}
              placeholder="Write your message here..."
              rows={6}
              className="mt-1"
            />
          </div>
          
          <Button onClick={handleSendManualEmail} disabled={sendingManualEmail}>
            {sendingManualEmail ? (
              <>Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />Send Email</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;
